import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError, formatCurrency, formatDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useAgency } from "@/context/AgencyContext";
import { StatusBadge } from "@/components/StatusBadge";
import { PolicyTypeBadge } from "@/components/PolicyTypeBadge";
import { ChatPanel } from "@/components/ChatPanel";
import { LiveClock } from "@/components/LiveClock";
import { Building2, LogOut, ShieldCheck, MessageSquare } from "lucide-react";
import { toast } from "sonner";

const TYPE_LABEL = {
  auto: "Auto", casa: "Casa", vita: "Vita", salute: "Salute",
  infortuni: "Infortuni", viaggio: "Viaggio", responsabilita_civile: "RC",
};

export default function CompanyArea() {
  const { user, logout } = useAuth();
  const { agency } = useAgency();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [conv, setConv] = useState(null);
  const [tab, setTab] = useState("messaggi"); // messaggi | polizze

  useEffect(() => {
    api.get("/my/company-portfolio")
      .then((r) => setData(r.data))
      .catch((e) => toast.error(formatApiError(e)));
    api.get("/my/conversation")
      .then((r) => setConv(r.data))
      .catch((e) => toast.error(formatApiError(e)));
  }, []);

  const onLogout = async () => {
    await logout();
    navigate("/login");
  };

  if (!data || !conv) return <div className="min-h-screen flex items-center justify-center text-slate-400">Caricamento…</div>;

  const totalPremium = data.policies.reduce((a, p) => a + (p.premium || 0), 0);

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="company-area">
      <header className="bg-[#0A192F] text-white">
        <div className="max-w-5xl mx-auto px-6 py-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-md bg-blue-600 flex items-center justify-center overflow-hidden">
              {agency?.logo_url ? (
                <img src={agency.logo_url} alt="logo" className="h-full w-full object-cover" />
              ) : (
                <Building2 className="h-5 w-5" />
              )}
            </div>
            <div>
              <div className="font-display font-semibold tracking-tight">{agency?.name || "Assicura"}</div>
              <div className="text-xs text-slate-400">Area Compagnia</div>
              <div data-testid="role-badge-portal" className="mt-2 inline-flex items-center text-[10px] uppercase tracking-[0.15em] font-bold px-2 py-0.5 rounded border bg-blue-400/15 text-blue-200 border-blue-300/30">
                🏛️ Compagnia
              </div>
            </div>
          </div>
          <button
            data-testid="company-logout"
            onClick={onLogout}
            className="text-xs px-3 py-1.5 rounded bg-white/10 hover:bg-white/20 inline-flex items-center gap-1.5"
          >
            <LogOut className="h-3.5 w-3.5" /> Esci
          </button>
        </div>
        <div className="max-w-5xl mx-auto px-6 pb-2">
          <div className="bg-white/5 rounded px-3 py-1.5 inline-block text-slate-300">
            <LiveClock inline />
          </div>
        </div>
        <div className="max-w-5xl mx-auto px-6 pb-8">
          <p className="text-xs uppercase tracking-[0.2em] text-blue-300 mb-2">Portafoglio assegnato</p>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight">{data.company?.name || user?.name}</h1>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 -mt-6 pb-12">
        <div className="bg-white border border-slate-200 rounded-lg p-3 flex gap-2 mb-4">
          <button
            data-testid="tab-messaggi"
            onClick={() => setTab("messaggi")}
            className={`flex-1 text-sm py-2 rounded-md transition-colors flex items-center justify-center gap-2 ${
              tab === "messaggi" ? "bg-blue-600 text-white" : "text-slate-600 hover:bg-slate-100"
            }`}
          >
            <MessageSquare className="h-4 w-4" />
            Messaggi
            {conv?.unread_count > 0 && (
              <span className="bg-red-500 text-white text-[10px] font-semibold rounded-full px-1.5 min-w-[18px] h-[18px] flex items-center justify-center">{conv.unread_count}</span>
            )}
          </button>
          <button
            data-testid="tab-polizze"
            onClick={() => setTab("polizze")}
            className={`flex-1 text-sm py-2 rounded-md transition-colors flex items-center justify-center gap-2 ${
              tab === "polizze" ? "bg-blue-600 text-white" : "text-slate-600 hover:bg-slate-100"
            }`}
          >
            <ShieldCheck className="h-4 w-4" />
            Polizze ({data.policies.length})
          </button>
        </div>

        {tab === "messaggi" && (
          <div className="bg-white border border-slate-200 rounded-lg overflow-hidden flex flex-col" style={{ minHeight: "70vh" }}>
            <ChatPanel
              conversationId={conv.id}
              partnerName={agency?.name || "Assicura"}
              partnerType="agency"
              currentUser={user}
            />
          </div>
        )}

        {tab === "polizze" && (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
              <div className="bg-white border border-slate-200 rounded-lg p-5">
                <p className="text-xs uppercase tracking-wide text-slate-500">Polizze attive</p>
                <p className="text-3xl font-display font-semibold mt-1 tabular-nums" data-testid="company-policies-count">{data.policies.length}</p>
              </div>
              <div className="bg-white border border-slate-200 rounded-lg p-5">
                <p className="text-xs uppercase tracking-wide text-slate-500">Premi totali</p>
                <p className="text-3xl font-display font-semibold mt-1 tabular-nums">{formatCurrency(totalPremium)}</p>
              </div>
            </div>

            <section>
              <h2 className="font-display text-xl font-semibold mb-4">Polizze intermediate</h2>
              <div className="space-y-3" data-testid="company-policies-list">
                {data.policies.length === 0 && (
                  <div className="bg-white border border-dashed border-slate-300 rounded-lg p-10 text-center text-slate-400">
                    Nessuna polizza intermediata.
                  </div>
                )}
                {data.policies.map((p) => (
                  <div key={p.id} className="bg-white border border-slate-200 rounded-lg p-5" data-testid={`company-policy-${p.id}`}>
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <PolicyTypeBadge code={p.type} size="md" testid={`company-type-badge-${p.id}`} />
                          <StatusBadge status={p.status} />
                        </div>
                        <p className="text-xs text-slate-500 mt-0.5">n° {p.policy_number} · cliente {p.client_name}</p>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                        <div>
                          <p className="text-xs uppercase tracking-wide text-slate-400">Premio</p>
                          <p className="font-medium tabular-nums mt-0.5">{formatCurrency(p.premium)}</p>
                        </div>
                        <div>
                          <p className="text-xs uppercase tracking-wide text-slate-400">Scadenza</p>
                          <p className="font-medium tabular-nums mt-0.5">{formatDate(p.end_date)}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          </>
        )}

        <footer className="mt-12 text-center text-xs text-slate-400">
          Accesso effettuato come <span className="font-medium">{user?.email}</span>
        </footer>
      </main>
    </div>
  );
}
